import { useQuery } from '@apollo/client';

import { MY_PROFILE_QUERY } from '../graphql/queries';

export interface UseMyProfileOptions {
  /**
   * Skip the query entirely. For callers mounted outside the candidate portal (`SessionAvatarSync`),
   * which must not query `myProfile` while signed out or for an account with no candidate role — the
   * server would reject it, and the global error link would surface that as a spurious failure.
   */
  skip?: boolean;
}

/**
 * Loads the authenticated candidate's own profile. `cache-and-network` because a candidate edits
 * their profile across several screens in one session and expects each to reflect the latest state
 * (docs/development/FrontendGuidelines.md §4.1).
 *
 * `MY_PROFILE_QUERY` is a generated `TypedDocumentNode`, so `data`/`error`/etc. are fully inferred
 * from the schema — no manual `useQuery<...>()` generic needed (§5.4).
 */
export function useMyProfile({ skip = false }: UseMyProfileOptions = {}) {
  const { data, loading, error, refetch } = useQuery(MY_PROFILE_QUERY, {
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  return {
    profile: data?.myProfile ?? null,
    loading,
    error: error ?? null,
    refetch,
  };
}
